import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaPaperPlane, FaImage, FaEllipsisV } from 'react-icons/fa';
import './Chat.css';

const Chat = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (chatId) {
      fetchChat(chatId);
    }
  }, [chatId]);

  useEffect(() => {
    scrollToBottom();
  }, [activeChat?.messages]);

  const getUserDisplayName = (userObj) => {
    if (!userObj) {
      return 'Unknown User';
    }
    
    // For sellers: Show "Business Name (Contact Person)" if both exist
    if (userObj.businessName) {
      const businessName = userObj.businessName;
      
      // Check if contact person name exists
      if (userObj.consumerProfile && (userObj.consumerProfile.firstName || userObj.consumerProfile.lastName)) {
        const firstName = userObj.consumerProfile.firstName || '';
        const lastName = userObj.consumerProfile.lastName || '';
        const contactPerson = `${firstName} ${lastName}`.trim();
        return `${businessName} (${contactPerson})`;
      }
      
      return businessName;
    }
    
    // For buyers/consumers: Use full name from consumerProfile
    if (userObj.consumerProfile && (userObj.consumerProfile.firstName || userObj.consumerProfile.lastName)) {
      const firstName = userObj.consumerProfile.firstName || '';
      const lastName = userObj.consumerProfile.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName;
    }
    
    // Fallback: Use email username part (before @)
    if (userObj.email) {
      const emailName = userObj.email.split('@')[0];
      // Make it more readable: replace + and _ with space, capitalize first letter
      const readableName = emailName
        .replace(/[+_]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return readableName;
    }
    
    // Fallback: Use phone if available
    if (userObj.phone) {
      return userObj.phone;
    }
    
    return 'Unknown User';
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/chat/conversations');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchChat = async (id) => {
    try {
      const response = await api.get(`/chat/${id}`);
      setActiveChat(response.data.chat);
    } catch (error) {
      console.error('Failed to fetch chat:', error);
      toast.error('Failed to load chat');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !activeChat) return;

    setSending(true);
    try {
      const response = await api.post(`/chat/${activeChat._id}/message`, {
        content: message.trim()
      });

      // Update local state
      setActiveChat(prev => ({
        ...prev,
        messages: [...prev.messages, response.data.message]
      }));

      setMessage('');
      fetchConversations(); // Refresh conversation list
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const formatMessageDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return d.toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'long', 
        year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined 
      });
    }
  };

  const shouldShowDateSeparator = (currentMsg, prevMsg) => {
    if (!prevMsg) return true;
    const currentDate = new Date(currentMsg.timestamp).toDateString();
    const prevDate = new Date(prevMsg.timestamp).toDateString();
    return currentDate !== prevDate;
  };

  const selectConversation = (conv) => {
    navigate(`/chat/${conv._id}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading chats...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="chat-container">
        {/* Conversations List */}
        <div className="conversations-sidebar">
          <div className="sidebar-header">
            <h2>Messages</h2>
            <span className="conversation-count">{conversations.length}</span>
          </div>

          <div className="conversations-list">
            {conversations.length === 0 ? (
              <div className="no-conversations">
                <p>No conversations yet</p>
                <p className="hint">Start chatting by clicking on a product or order</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv._id}
                  className={`conversation-item ${activeChat?._id === conv._id ? 'active' : ''}`}
                  onClick={() => selectConversation(conv)}
                >
                  <div className="conversation-avatar">
                    {conv.otherParticipant?.profileImage ? (
                      <img src={conv.otherParticipant.profileImage} alt="" />
                    ) : (
                      <div className="avatar-placeholder">
                        {getUserDisplayName(conv.otherParticipant).charAt(0)}
                      </div>
                    )}
                    {conv.unreadCount > 0 && (
                      <span className="unread-badge">{conv.unreadCount}</span>
                    )}
                  </div>

                  <div className="conversation-info">
                    <div className="conversation-header">
                      <h4>{getUserDisplayName(conv.otherParticipant)}</h4>
                      <span className="time">{formatTime(conv.lastMessage?.timestamp)}</span>
                    </div>
                    <p className="last-message">
                      {conv.lastMessage?.content?.substring(0, 50) || 'No messages yet'}
                      {conv.lastMessage?.content?.length > 50 ? '...' : ''}
                    </p>
                    {conv.product && (
                      <span className="conversation-tag">
                        📦 {conv.product.title?.substring(0, 30)}
                      </span>
                    )}
                    {conv.order && (
                      <span className="conversation-tag">
                        🛍️ Order #{conv.order.orderNumber}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="chat-area">
          {activeChat ? (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <button className="btn-back-mobile" onClick={() => navigate('/chat')}>
                  <FaArrowLeft />
                </button>
                <div className="chat-participant-info">
                  <div className="participant-avatar">
                    {activeChat.participants?.find(p => p._id !== user?._id)?.profileImage ? (
                      <img src={activeChat.participants.find(p => p._id !== user?._id).profileImage} alt="" />
                    ) : (
                      <div className="avatar-placeholder">
                        {getUserDisplayName(activeChat.participants?.find(p => p._id !== user?._id)).charAt(0)}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3>{getUserDisplayName(activeChat.participants?.find(p => p._id !== user?._id))}</h3>
                    <p className="participant-type">
                      {activeChat.participants?.find(p => p._id !== user?._id)?.userType}
                    </p>
                  </div>
                </div>
                <button className="btn-icon">
                  <FaEllipsisV />
                </button>
              </div>

              {/* Messages */}
              <div className="chat-messages">
                {/* Product Context Card */}
                {activeChat.product && (
                  <div className="product-context-card">
                    <div className="product-context-content">
                      {activeChat.product.images?.[0] && (
                        <img 
                          src={activeChat.product.images[0]} 
                          alt={activeChat.product.title}
                          className="product-context-image"
                        />
                      )}
                      <div className="product-context-info">
                        <span className="product-context-label">Discussing about</span>
                        <h4>{activeChat.product.title}</h4>
                        <p className="product-context-price">
                          ₹{activeChat.product.pricing?.basePrice} / {activeChat.product.pricing?.unit}
                        </p>
                      </div>
                    </div>
                    <button 
                      className="btn-view-product"
                      onClick={() => navigate(`/products/${activeChat.product._id}`)}
                    >
                      View Product
                    </button>
                  </div>
                )}

                {activeChat.messages?.length === 0 ? (
                  <div className="no-messages">
                    <p>Start a conversation</p>
                  </div>
                ) : (
                  activeChat.messages.map((msg, index) => {
                    const isSent = msg.sender === user?._id;
                    const sender = activeChat.participants?.find(p => p._id === msg.sender);
                    const showAvatar = !isSent;
                    const prevMsg = index > 0 ? activeChat.messages[index - 1] : null;
                    const showDateSeparator = shouldShowDateSeparator(msg, prevMsg);
                    
                    return (
                      <React.Fragment key={index}>
                        {showDateSeparator && (
                          <div className="date-separator">
                            <span>{formatMessageDate(msg.timestamp)}</span>
                          </div>
                        )}
                        <div
                          className={`message-wrapper ${isSent ? 'sent' : 'received'}`}
                        >
                          {showAvatar && (
                            <div className="message-avatar">
                              {sender?.profileImage ? (
                                <img src={sender.profileImage} alt={getUserDisplayName(sender)} />
                              ) : (
                                <div className="avatar-circle">
                                  {getUserDisplayName(sender).charAt(0)}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="message-bubble">
                            {!isSent && (
                              <span className="sender-name">{getUserDisplayName(sender)}</span>
                            )}
                            <div className="message-content">
                              <p>{msg.content}</p>
                            </div>
                            <div className="message-footer">
                              <span className="message-time">
                                {new Date(msg.timestamp).toLocaleTimeString('en-IN', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              {isSent && (
                                <span className="message-status">
                                  {msg.read ? '✓✓' : '✓'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form className="chat-input" onSubmit={sendMessage}>
                <button type="button" className="btn-icon" disabled>
                  <FaImage />
                </button>
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={sending}
                />
                <button type="submit" className="btn-send" disabled={sending || !message.trim()}>
                  <FaPaperPlane />
                </button>
              </form>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="empty-state">
                <h3>Select a conversation</h3>
                <p>Choose a conversation from the list to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Chat;
